/**
 * A table is a map-like collection. But unlike a traditional collection, it's keys and values are
 * not stored within the `Table` value, but instead are stored using Sui's object system. The
 * `Table` struct acts only as a handle into the object system to retrieve those keys and values.
 * Note that this means that `Table` values with exactly the same key-value mapping will not be
 * equal, with `==`, at runtime. For example
 * ```
 * let table1 = table::new<u64, bool>();
 * let table2 = table::new<u64, bool>();
 * table::add(&mut table1, 0, false);
 * table::add(&mut table1, 1, true);
 * table::add(&mut table2, 0, false);
 * table::add(&mut table2, 1, true);
 * // table1 does not equal table2, despite having the same entries
 * assert!(&table1 != &table2);
 * ```
 */

import { bcs } from '@mysten/sui/bcs'
import type { ClientWithCoreApi, SuiClientTypes } from '@mysten/sui/client'
import type { SuiObjectData, SuiParsedData } from '@mysten/sui/jsonRpc'
import { fromBase64 } from '@mysten/sui/utils'
import {
  assertFieldsWithTypesArgsMatch,
  assertReifiedTypeArgsMatch,
  decodeFromFields,
  decodeFromFieldsWithTypes,
  decodeFromJSONField,
  extractType,
  phantom,
  PhantomReified,
  PhantomToTypeStr,
  PhantomTypeArgument,
  Reified,
  StructClass,
  ToField,
  ToJSON,
  ToPhantomTypeArgument,
  ToTypeStr,
} from '../../_framework/reified'
import {
  composeSuiType,
  compressSuiType,
  FieldsWithTypes,
  parseTypeName,
} from '../../_framework/util'
import { UID } from '../object/structs'

/* ============================== Table =============================== */

export function isTable(type: string): boolean {
  type = compressSuiType(type)
  return type.startsWith(`0x2::table::Table` + '<')
}

export interface TableFields<K extends PhantomTypeArgument, V extends PhantomTypeArgument> {
  /** the ID of this table */
  id: ToField<UID>
  /** the number of key-value pairs in the table */
  size: ToField<'u64'>
}

export type TableReified<K extends PhantomTypeArgument, V extends PhantomTypeArgument> = Reified<
  Table<K, V>,
  TableFields<K, V>
>

export type TableJSONField<K extends PhantomTypeArgument, V extends PhantomTypeArgument> = {
  id: string
  size: string
}

export type TableJSON<K extends PhantomTypeArgument, V extends PhantomTypeArgument> = {
  $typeName: typeof Table.$typeName
  $typeArgs: [PhantomToTypeStr<K>, PhantomToTypeStr<V>]
} & TableJSONField<K, V>

export class Table<K extends PhantomTypeArgument, V extends PhantomTypeArgument>
  implements StructClass
{
  __StructClass = true as const

  static readonly $typeName: `0x2::table::Table` = `0x2::table::Table` as const
  static readonly $numTypeParams = 2
  static readonly $isPhantom = [true, true] as const

  readonly $typeName: typeof Table.$typeName = Table.$typeName
  readonly $fullTypeName: `0x2::table::Table<${PhantomToTypeStr<K>}, ${PhantomToTypeStr<V>}>`
  readonly $typeArgs: [PhantomToTypeStr<K>, PhantomToTypeStr<V>]
  readonly $isPhantom: typeof Table.$isPhantom = Table.$isPhantom

  /** the ID of this table */
  readonly id: ToField<UID>
  /** the number of key-value pairs in the table */
  readonly size: ToField<'u64'>

  private constructor(
    typeArgs: [PhantomToTypeStr<K>, PhantomToTypeStr<V>],
    fields: TableFields<K, V>,
  ) {
    this.$fullTypeName = composeSuiType(
      Table.$typeName,
      ...typeArgs,
    ) as `0x2::table::Table<${PhantomToTypeStr<K>}, ${PhantomToTypeStr<V>}>`
    this.$typeArgs = typeArgs

    this.id = fields.id
    this.size = fields.size
  }

  static reified<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    K: K,
    V: V,
  ): TableReified<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    const reifiedBcs = Table.bcs
    return {
      get typeName() {
        return Table.$typeName
      },
      get fullTypeName() {
        return composeSuiType(
          Table.$typeName,
          ...[extractType(K), extractType(V)],
        ) as `0x2::table::Table<${PhantomToTypeStr<ToPhantomTypeArgument<K>>}, ${PhantomToTypeStr<
          ToPhantomTypeArgument<V>
        >}>`
      },
      get typeArgs() {
        return [extractType(K), extractType(V)] as [
          PhantomToTypeStr<ToPhantomTypeArgument<K>>,
          PhantomToTypeStr<ToPhantomTypeArgument<V>>,
        ]
      },
      isPhantom: Table.$isPhantom,
      reifiedTypeArgs: [K, V],
      fromFields: (fields: Record<string, any>) => Table.fromFields([K, V], fields),
      fromFieldsWithTypes: (item: FieldsWithTypes) => Table.fromFieldsWithTypes([K, V], item),
      fromBcs: (data: Uint8Array) => Table.fromFields([K, V], reifiedBcs.parse(data)),
      bcs: reifiedBcs,
      fromJSONField: (field: any) => Table.fromJSONField([K, V], field),
      fromJSON: (json: Record<string, any>) => Table.fromJSON([K, V], json),
      fromCoreObject: (obj: SuiClientTypes.Object<{ content: true }>) =>
        Table.fromCoreObject([K, V], obj),
      fromSuiParsedData: (content: SuiParsedData) => Table.fromSuiParsedData([K, V], content),
      fromSuiObjectData: (content: SuiObjectData) => Table.fromSuiObjectData([K, V], content),
      fetch: async (client: ClientWithCoreApi, id: string) => Table.fetch(client, [K, V], id),
      new: (fields: TableFields<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>>) => {
        return new Table([extractType(K), extractType(V)], fields)
      },
      kind: 'StructClassReified',
    }
  }

  static get r(): typeof Table.reified {
    return Table.reified
  }

  static phantom<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    K: K,
    V: V,
  ): PhantomReified<ToTypeStr<Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>>>> {
    return phantom(Table.reified(K, V))
  }

  static get p(): typeof Table.phantom {
    return Table.phantom
  }

  private static instantiateBcs() {
    return bcs.struct('Table', {
      id: UID.bcs,
      size: bcs.u64(),
    })
  }

  private static cachedBcs: ReturnType<typeof Table.instantiateBcs> | null = null

  static get bcs(): ReturnType<typeof Table.instantiateBcs> {
    if (!Table.cachedBcs) {
      Table.cachedBcs = Table.instantiateBcs()
    }
    return Table.cachedBcs
  }

  static fromFields<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    fields: Record<string, any>,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    return Table.reified(typeArgs[0], typeArgs[1]).new({
      id: decodeFromFields(UID.reified(), fields.id),
      size: decodeFromFields('u64', fields.size),
    })
  }

  static fromFieldsWithTypes<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    item: FieldsWithTypes,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    if (!isTable(item.type)) {
      throw new Error('not a Table type')
    }
    assertFieldsWithTypesArgsMatch(item, typeArgs)

    return Table.reified(typeArgs[0], typeArgs[1]).new({
      id: decodeFromFieldsWithTypes(UID.reified(), item.fields.id),
      size: decodeFromFieldsWithTypes('u64', item.fields.size),
    })
  }

  static fromBcs<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    data: Uint8Array,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    return Table.fromFields(typeArgs, Table.bcs.parse(data))
  }

  toJSONField(): TableJSONField<K, V> {
    return {
      id: this.id,
      size: this.size.toString(),
    }
  }

  toJSON(): TableJSON<K, V> {
    return { $typeName: this.$typeName, $typeArgs: this.$typeArgs, ...this.toJSONField() }
  }

  static fromJSONField<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    field: any,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    return Table.reified(typeArgs[0], typeArgs[1]).new({
      id: decodeFromJSONField(UID.reified(), field.id),
      size: decodeFromJSONField('u64', field.size),
    })
  }

  static fromJSON<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    json: Record<string, any>,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    if (json.$typeName !== Table.$typeName) {
      throw new Error(
        `not a Table json object: expected '${Table.$typeName}' but got '${json.$typeName}'`,
      )
    }
    assertReifiedTypeArgsMatch(
      composeSuiType(Table.$typeName, ...typeArgs.map(extractType)),
      json.$typeArgs,
      typeArgs,
    )

    return Table.fromJSONField(typeArgs, json)
  }

  static fromCoreObject<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    obj: SuiClientTypes.Object<{ content: true }>,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    if (!isTable(obj.type)) {
      throw new Error(`object at ${obj.objectId} is not a Table object`)
    }

    const gotTypeArgs = parseTypeName(obj.type).typeArgs
    if (gotTypeArgs.length !== 2) {
      throw new Error(
        `type argument mismatch: expected 2 type arguments but got '${gotTypeArgs.length}'`,
      )
    }
    for (let i = 0; i < 2; i++) {
      const gotTypeArg = compressSuiType(gotTypeArgs[i])
      const expectedTypeArg = compressSuiType(extractType(typeArgs[i]))
      if (gotTypeArg !== expectedTypeArg) {
        throw new Error(
          `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
        )
      }
    }

    return Table.fromBcs(typeArgs, obj.content)
  }

  /** @deprecated `SuiParsedData` is a JSON-RPC-only type that is being phased out upstream. Use {@link Table.fromCoreObject} together with `client.core.getObject({ include: { content: true } })` for transport-agnostic parsing. */
  static fromSuiParsedData<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    content: SuiParsedData,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    if (content.dataType !== 'moveObject') {
      throw new Error('not an object')
    }
    if (!isTable(content.type)) {
      throw new Error(`object at ${(content.fields as any).id} is not a Table object`)
    }
    return Table.fromFieldsWithTypes(typeArgs, content)
  }

  /** @deprecated `SuiObjectData` is a JSON-RPC-only type that is being phased out upstream. Use {@link Table.fromCoreObject} together with `client.core.getObject({ include: { content: true } })` for transport-agnostic parsing. */
  static fromSuiObjectData<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    typeArgs: [K, V],
    data: SuiObjectData,
  ): Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>> {
    if (data.bcs) {
      if (data.bcs.dataType !== 'moveObject' || !isTable(data.bcs.type)) {
        throw new Error(`object at is not a Table object`)
      }

      const gotTypeArgs = parseTypeName(data.bcs.type).typeArgs
      if (gotTypeArgs.length !== 2) {
        throw new Error(
          `type argument mismatch: expected 2 type arguments but got '${gotTypeArgs.length}'`,
        )
      }
      for (let i = 0; i < 2; i++) {
        const gotTypeArg = compressSuiType(gotTypeArgs[i])
        const expectedTypeArg = compressSuiType(extractType(typeArgs[i]))
        if (gotTypeArg !== expectedTypeArg) {
          throw new Error(
            `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
          )
        }
      }

      return Table.fromBcs(typeArgs, fromBase64(data.bcs.bcsBytes))
    }
    if (data.content) {
      return Table.fromSuiParsedData(typeArgs, data.content)
    }
    throw new Error(
      'Both `bcs` and `content` fields are missing from the data. Include `showBcs` or `showContent` in the request.',
    )
  }

  static async fetch<
    K extends PhantomReified<PhantomTypeArgument>,
    V extends PhantomReified<PhantomTypeArgument>,
  >(
    client: ClientWithCoreApi,
    typeArgs: [K, V],
    id: string,
  ): Promise<Table<ToPhantomTypeArgument<K>, ToPhantomTypeArgument<V>>> {
    const { object } = await client.core.getObject({
      objectId: id,
      include: { content: true },
    })
    if (!isTable(object.type)) {
      throw new Error(`object at id ${id} is not a Table object`)
    }

    const gotTypeArgs = parseTypeName(object.type).typeArgs
    if (gotTypeArgs.length !== 2) {
      throw new Error(
        `type argument mismatch: expected 2 type arguments but got '${gotTypeArgs.length}'`,
      )
    }
    for (let i = 0; i < 2; i++) {
      const gotTypeArg = compressSuiType(gotTypeArgs[i])
      const expectedTypeArg = compressSuiType(extractType(typeArgs[i]))
      if (gotTypeArg !== expectedTypeArg) {
        throw new Error(
          `type argument mismatch at position ${i}: expected '${expectedTypeArg}' but got '${gotTypeArg}'`,
        )
      }
    }

    return Table.fromBcs(typeArgs, object.content)
  }
}
